import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { MENU_TYPE_MED, MENU_TYPE_REC } from 'shared/constants';
import { getPersistedValue, setPersistedValue } from 'shared/utils/persisted-values';
import { getCityToDisplay } from 'shared/helpers/address';

import useTranslation from 'hooks/use-translation';
import { useIsMounted } from 'hooks/use-is-mounted';
import useUI from 'hooks/use-ui';
import useCart from 'hooks/use-cart';
import useSearchPreferences from 'hooks/use-search-preferences';
import { useDispensariesQueryParams } from 'hooks/use-dispensaries-query-params';
import { useDispensaryFilterPillOptions } from 'hooks/use-filter-pill-options';
import { useDispensaries } from './hooks/use-dispensaries';
import { getDispensariesHeading } from './utils';

import Dispensaries from './dispensaries';
import DispensariesRedesign from './dispensaries-redesign';

function useAddress({ location, doNotRedirectOnMissingAddress }) {
  const { showHome } = useUI();
  const { updateAddress } = useCart();
  const isMounted = useIsMounted();

  const addressObj = location ?? getPersistedValue(`address`);

  const setUpAddress = useCallback(() => {
    if (location) {
      updateAddress({ location, residentialStatus: 'VERIFIED' });
    }
  }, [updateAddress, location]);

  const redirectIfMissingAddress = useCallback(() => {
    if (!doNotRedirectOnMissingAddress && (!addressObj?.lat || !addressObj?.lng)) {
      showHome();
    }
  }, [addressObj?.lat, addressObj?.lng, showHome, doNotRedirectOnMissingAddress]);

  useEffect(setUpAddress, [setUpAddress]);
  useEffect(redirectIfMissingAddress, [redirectIfMissingAddress]);

  // `getPersistedValue('address')` reads localStorage, which is empty on the server. To avoid an
  // SSR hydration mismatch in the rendered heading (server: "Dispensaries" vs client:
  // "Birmingham, MI Dispensaries"), only surface the client-only persisted address for display
  // AFTER mount. `location` (when provided via the route) is known on both sides, so it is used
  // immediately. The redirect effect above still uses the real `addressObj` (it only runs client-side).
  const displayAddress = isMounted ? addressObj : location ?? null;

  return {
    city: getCityToDisplay(displayAddress),
    state: displayAddress?.state,
    stateLong: displayAddress?.stateLong,
  };
}

function useMenuType() {
  const { setMenuType } = useCart();
  const {
    queryParams: { recreational, medical },
  } = useDispensariesQueryParams();

  useEffect(() => {
    if (recreational && !medical) {
      setMenuType(MENU_TYPE_REC);
    }
    if (medical && !recreational) {
      setMenuType(MENU_TYPE_MED);
    }
  }, [setMenuType, medical, recreational]);
}

type DispensariesPageEntryProps = {
  location?: Record<string, unknown>;
  doNotRedirectOnMissingAddress?: boolean;
};

export const DispensariesPageEntry = observer(
  ({ location, doNotRedirectOnMissingAddress = false }: DispensariesPageEntryProps) => {
    const flags = useFlags();
    const useRedesign = !!flags['growth.ecomm.dispensaries-v2.rollout'];

    const { t } = useTranslation();
    const { isPickup } = useSearchPreferences();
    const { dispensaries, loading: dispensariesLoading } = useDispensaries({ isPickup });
    const filterPillOptions = useDispensaryFilterPillOptions({ isPickup });

    const { city, state, stateLong } = useAddress({ location, doNotRedirectOnMissingAddress });
    useMenuType();

    useEffect(() => {
      setPersistedValue(`dispensariesSearchParams`, '');
    }, []);

    useEffect(() => {
      window.scrollTo(0, 0);
    }, []);

    const heading = getDispensariesHeading(t('common.dispensaries', 'Dispensaries'), city, state, stateLong);

    const props = { dispensaries, dispensariesLoading, filterPillOptions, heading };

    return useRedesign ? <DispensariesRedesign {...props} /> : <Dispensaries {...props} />;
  }
);
